export function csrfTokenMetaElement() {
    return document.head.querySelector("meta[name='csrf-token']");
}

/**
 * @return {String} The value of the 'csrf-token' meta element, if there is one.
 */
export function csrfToken() {
    const meta = csrfTokenMetaElement();
    return meta ? meta.content : undefined;
}

async function sendJSONWithoutBody(method, url) {
    const headers = { "Accept": "application/json" };

    const token = csrfToken();
    if (method !== "GET" && token) {
        headers["X-CSRF-Token"] = token;
    }

    const response = await fetch(url, {
        method: method,
        headers
    });

    return response.json();
}

async function sendJSONWithBody(method, url, data) {
    const headers = { "Content-Type": "application/json" };

    const token = csrfToken();
    if (token) {
        headers["X-CSRF-Token"] = token;
    }

    const response = await fetch(url, {
        method: method,
        headers,
        body: JSON.stringify(data),
    });

    return response.json();
}

class TurboResponseError extends Error {
    constructor(response) {
        super(response.statusText);
        this.response = response;
    }
}

/**
 * Serialise form data and submit via a POST request. Returns a Promise
 * that resolves the result of parsing the response as raw text.
 * @param url
 * @param data
 * @returns {Promise<string>}
 */
export async function postTurboForm(url, data) {
    const token = csrfToken();
    const headers = {
        "Accept": "text/vnd.turbo-stream.html",
        "X-CSRF-Token": token
    };

    const formData = new FormData();
    for (const [key, value] of Object.entries(data)) {
        if (Array.isArray(value)) {
            value.forEach(v => formData.append(key, v));
        } else {
            formData.append(key, value);
        }
    }

    const response = await fetch(url, {
        method: "POST",
        headers,
        body: formData
    });

    return response.text();
}

/**
 * Serialise form data and submit a Turbo Stream via a PUT request. Returns a
 * Promise that resolves the result of parsing the response as raw text.
 */
export async function putTurboForm(url, data) {
    const token = csrfToken();
    const headers = {
        "Accept": "text/vnd.turbo-stream.html",
        "X-CSRF-Token": token
    };

    const formData = new FormData();
    for (const [key, value] of Object.entries(data)) {
        if (Array.isArray(value)) {
            value.forEach(v => formData.append(key, v));
        } else {
            formData.append(key, value);
        }
    }

    const response = await fetch(url, {
        method: "PUT",
        headers,
        body: formData
    });

    // If there's an error, we don't want to send the response to Turbo, otherwise
    // it will try to render an error and break the page. This way we can catch
    // the error and display a message or fallback instead.
    if (!response.ok) {
        throw new TurboResponseError(response);
    }

    return response.text();
}

/**
* Fetches a Turbo Stream via a GET request. Returns a Promise that resolves
* the result of parsing the response as raw text.
* @param url
* @returns {Promise<string>}
*/
export async function getTurboStream(url) {
    const token = csrfToken();
    const headers = {
        "Accept": "text/vnd.turbo-stream.html",
        "X-CSRF-Token": token
    };

    const response = await fetch(url, {
        method: "GET",
        headers
    });

    return response.text();
}

/**
 * Makes a HTTP request and returns a Promise that resolves with
 * the result of parsing the response as JSON.
 *
 * @param {String} url - The relative path or absolute URL endpoint
 * @return {Promise}
 */
export async function getJSON(url) {
    const json = await sendJSONWithoutBody("GET", url);

    return json;
}

/**
 * Serialise object data and submit via a POST request. Returns a Promise
 * that resolves the result of parsing the response as JSON.
 *
 * @param {String} url - The relative path or absolute URL endpoint
 * @param {Object} data - The POST data object (will be JSON serialised)
 * @return {Promise}
 */
export async function postJSON(url, data) {
    const json = await sendJSONWithBody("POST", url, data);

    return json;
}

/**
 * Serialise object data and submit via a PUT request. Returns a Promise
 * that resolves the result of parsing the response as JSON.
 *
 * @param {String} url - The relative path or absolute URL endpoint
 * @param {Object} data - The PUT data object (will be JSON serialised)
 * @return {Promise}
 */
export async function putJSON(url, data) {
    await sendJSONWithBody("PUT", url, data);
}

/**
 * Serialise object data and submit via a DELETE request. Returns a Promise
 * that resolves the result of parsing the response as JSON.
 *
 * @param {String} url - The relative path or absolute URL endpoint
 * @return {Promise}
 */
export async function deleteJSON(url) {
    await sendJSONWithoutBody("DELETE", url);
}
